import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookOpenIcon, CalendarIcon, ChevronRightIcon, ClockIcon, GlobeIcon, MailIcon, MessageSquareIcon, StarIcon, TrophyIcon, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Sitemap } from '../components/Sitemap';
// Sample user data - in a real app, this would come from an API or context
const userData = {
  name: 'Sarah Ahmed',
  username: '@sarahahmed',
  email: 'sarah.ahmed@example.com',
  location: 'Cairo, Egypt',
  bio: 'English learner passionate about improving my language skills. Currently focusing on business English and conversation practice.',
  profilePicture: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  coverImage: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
  level: 'Intermediate',
  joinedDate: 'January 2024',
  stats: {
    lessonsCompleted: 42,
    studyHours: 78,
    streak: 15,
    vocabulary: 650,
    exercises: 124
  },
  badges: [{
    id: 1,
    name: 'Grammar Master',
    icon: 'trophy',
    description: 'Completed all grammar lessons',
    date: '2 weeks ago'
  }, {
    id: 2,
    name: '10-Day Streak',
    icon: 'fire',
    description: 'Studied for 10 consecutive days',
    date: '1 month ago'
  }, {
    id: 3,
    name: 'Vocabulary Builder',
    icon: 'book',
    description: 'Learned 500+ words',
    date: '2 months ago'
  }],
  recentActivity: [{
    id: 1,
    type: 'lesson',
    title: 'Business English: Email Writing',
    date: '2 days ago',
    score: '95%'
  }, {
    id: 2,
    type: 'exercise',
    title: 'Past Tense Practice Quiz',
    date: '4 days ago',
    score: '85%'
  }, {
    id: 3,
    type: 'forum',
    title: 'Posted in "Pronunciation Help"',
    date: '1 week ago',
    replies: 3
  }, {
    id: 4,
    type: 'vocabulary',
    title: 'Added 20 new words',
    date: '1 week ago',
    category: 'Business Terms'
  }],
  learningPath: {
    currentCourse: 'Intermediate Business English',
    progress: 65,
    nextLesson: 'Professional Presentations',
    recommendedLessons: [{
      id: 1,
      title: 'Email Etiquette',
      duration: '15 min',
      level: 'Intermediate'
    }, {
      id: 2,
      title: 'Phone Conversations',
      duration: '20 min',
      level: 'Intermediate'
    }]
  }
};
export function ProfilePage() {
  const {
    isArabic
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3959">
      <Sitemap currentPage={{
      en: 'Profile',
      ar: 'الملف الشخصي'
    }} data-id="element-3960" />
      <div className="max-w-7xl mx-auto" data-id="element-3961">
        {/* Profile Header with Cover Image */}
        <div className="relative rounded-xl overflow-hidden mb-8" data-id="element-3962">
          <div className="h-48 md:h-64 bg-gradient-to-r from-blue-400 to-indigo-500 relative" data-id="element-3963">
            <img src={userData.coverImage} alt="Cover" className="w-full h-full object-cover opacity-70" data-id="element-3964" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-3965"></div>
          {/* Profile Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white" data-id="element-3966">
            <div className="flex flex-col md:flex-row md:items-end gap-4" data-id="element-3967">
              <div className="relative" data-id="element-3968">
                <img src={userData.profilePicture} alt={userData.name} className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg object-cover" data-id="element-3969" />
                <div className="absolute bottom-0 right-0 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-3970">
                  {userData.level}
                </div>
              </div>
              <div className="flex-1" data-id="element-3971">
                <h1 className="text-2xl md:text-3xl font-bold mb-1" data-id="element-3972">
                  {userData.name}
                </h1>
                <p className="text-gray-200 text-sm md:text-base mb-2" data-id="element-3973">
                  {userData.username}
                </p>
                <div className="flex items-center gap-4 text-sm" data-id="element-3974">
                  <div className="flex items-center gap-1" data-id="element-3975">
                    <GlobeIcon className="w-4 h-4" data-id="element-3976" />
                    <span data-id="element-3977">{userData.location}</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-3978">
                    <CalendarIcon className="w-4 h-4" data-id="element-3979" />
                    <span data-id="element-3980">
                      {isArabic ? 'انضم في' : 'Joined'} {userData.joinedDate}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-4 md:mt-0 flex gap-3" data-id="element-3981">
                <Link to="/profile/settings" className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-white text-sm font-medium transition-colors" data-id="element-3982">
                  {isArabic ? 'تعديل الملف الشخصي' : 'Edit Profile'}
                </Link>
              </div>
            </div>
          </div>
        </div>
        {/* Profile Navigation Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8" data-id="element-3983">
          <div className="flex overflow-x-auto space-x-8" data-id="element-3984">
            <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'overview' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-3985">
              {isArabic ? 'نظرة عامة' : 'Overview'}
            </button>
            <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'progress' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('progress')} data-id="element-3986">
              {isArabic ? 'التقدم' : 'Progress'}
            </button>
            <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'achievements' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('achievements')} data-id="element-3987">
              {isArabic ? 'الإنجازات' : 'Achievements'}
            </button>
            <button className={`py-4 text-sm font-medium border-b-2 ${activeTab === 'activity' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('activity')} data-id="element-3988">
              {isArabic ? 'النشاط' : 'Activity'}
            </button>
          </div>
        </div>
        {/* Profile Content */}
        {activeTab === 'overview' && <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3989">
            {/* Left Column */}
            <div className="space-y-8" data-id="element-3990">
              {/* About */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-3991">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-3992">
                  {isArabic ? 'عني' : 'About'}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-3993">
                  {userData.bio}
                </p>
                <div className="space-y-3" data-id="element-3994">
                  <div className="flex items-center gap-3" data-id="element-3995">
                    <MailIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-3996" />
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-3997">
                      {userData.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3998">
                    <GlobeIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-3999" />
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-4000">
                      {userData.location}
                    </span>
                  </div>
                </div>
              </div>
              {/* Stats */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4001">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-4002">
                  {isArabic ? 'الإحصائيات' : 'Stats'}
                </h2>
                <div className="grid grid-cols-2 gap-4" data-id="element-4003">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-4004">
                    <div className="text-blue-600 dark:text-blue-400 text-2xl font-bold" data-id="element-4005">
                      {userData.stats.lessonsCompleted}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4006">
                      {isArabic ? 'الدروس المكتملة' : 'Lessons Completed'}
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4" data-id="element-4007">
                    <div className="text-green-600 dark:text-green-400 text-2xl font-bold" data-id="element-4008">
                      {userData.stats.studyHours}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4009">
                      {isArabic ? 'ساعات الدراسة' : 'Study Hours'}
                    </div>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4" data-id="element-4010">
                    <div className="text-amber-600 dark:text-amber-400 text-2xl font-bold" data-id="element-4011">
                      {userData.stats.streak}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4012">
                      {isArabic ? 'أيام متتالية' : 'Day Streak'}
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-4013">
                    <div className="text-purple-600 dark:text-purple-400 text-2xl font-bold" data-id="element-4014">
                      {userData.stats.vocabulary}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4015">
                      {isArabic ? 'المفردات المتعلمة' : 'Words Learned'}
                    </div>
                  </div>
                </div>
              </div>
              {/* Recent Badges */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4016">
                <div className="flex items-center justify-between mb-4" data-id="element-4017">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-4018">
                    {isArabic ? 'الشارات الأخيرة' : 'Recent Badges'}
                  </h2>
                  <button onClick={() => setActiveTab('achievements')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-4019">
                    {isArabic ? 'عرض الكل' : 'View all'}
                  </button>
                </div>
                <div className="space-y-4" data-id="element-4020">
                  {userData.badges.slice(0, 2).map(badge => <div key={badge.id} className="flex items-center gap-4" data-id="element-4021">
                      <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center" data-id="element-4022">
                        <TrophyIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-4023" />
                      </div>
                      <div data-id="element-4024">
                        <div className="font-medium text-gray-900 dark:text-white" data-id="element-4025">
                          {badge.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4026">
                          {badge.date}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Right Column */}
            <div className="md:col-span-2 space-y-8" data-id="element-4027">
              {/* Learning Path */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4028">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-4029">
                  {isArabic ? 'مسار التعلم' : 'Learning Path'}
                </h2>
                <div className="mb-6" data-id="element-4030">
                  <div className="flex items-center justify-between mb-2" data-id="element-4031">
                    <div className="text-gray-700 dark:text-gray-300" data-id="element-4032">
                      {userData.learningPath.currentCourse}
                    </div>
                    <div className="text-sm font-medium text-blue-600 dark:text-blue-400" data-id="element-4033">
                      {userData.learningPath.progress}%
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-4034">
                    <div className="bg-blue-600 dark:bg-blue-500 h-2.5 rounded-full" style={{
                  width: `${userData.learningPath.progress}%`
                }} data-id="element-4035"></div>
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6" data-id="element-4036">
                  <div className="flex items-center justify-between" data-id="element-4037">
                    <div data-id="element-4038">
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4039">
                        {isArabic ? 'الدرس التالي' : 'Next Lesson'}
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white" data-id="element-4040">
                        {userData.learningPath.nextLesson}
                      </div>
                    </div>
                    <Link to="/learn/path" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-4041">
                      {isArabic ? 'متابعة' : 'Continue'}
                    </Link>
                  </div>
                </div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-3" data-id="element-4042">
                  {isArabic ? 'موصى به لك' : 'Recommended for You'}
                </h3>
                <div className="space-y-3" data-id="element-4043">
                  {userData.learningPath.recommendedLessons.map(lesson => <div key={lesson.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-4044">
                      <div className="flex items-center gap-3" data-id="element-4045">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-4046">
                          <BookOpenIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4047" />
                        </div>
                        <div data-id="element-4048">
                          <div className="font-medium text-gray-900 dark:text-white" data-id="element-4049">
                            {lesson.title}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400" data-id="element-4050">
                            <span data-id="element-4051">{lesson.level}</span>
                            <span className="flex items-center gap-1" data-id="element-4052">
                              <ClockIcon className="w-3 h-3" data-id="element-4053" />
                              {lesson.duration}
                            </span>
                          </div>
                        </div>
                      </div>
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-4054" />
                    </div>)}
                </div>
              </div>
              {/* Recent Activity */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4055">
                <div className="flex items-center justify-between mb-4" data-id="element-4056">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-4057">
                    {isArabic ? 'النشاط الأخير' : 'Recent Activity'}
                  </h2>
                  <button onClick={() => setActiveTab('activity')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-4058">
                    {isArabic ? 'عرض الكل' : 'View all'}
                  </button>
                </div>
                <div className="space-y-4" data-id="element-4059">
                  {userData.recentActivity.map(activity => <div key={activity.id} className="flex items-start gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-4060">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mt-1" data-id="element-4061">
                        {activity.type === 'lesson' && <BookOpenIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4062" />}
                        {activity.type === 'exercise' && <ClipboardCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-4063" />}
                        {activity.type === 'forum' && <MessageSquareIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-4064" />}
                        {activity.type === 'vocabulary' && <BookIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-4065" />}
                      </div>
                      <div className="flex-1" data-id="element-4066">
                        <div className="font-medium text-gray-900 dark:text-white" data-id="element-4067">
                          {activity.title}
                        </div>
                        <div className="flex items-center justify-between mt-1" data-id="element-4068">
                          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4069">
                            {activity.date}
                          </div>
                          {activity.score && <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-4070">
                              {activity.score}
                            </div>}
                          {activity.replies && <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1" data-id="element-4071">
                              <MessageSquareIcon className="w-4 h-4" data-id="element-4072" />
                              {activity.replies} {isArabic ? 'ردود' : 'replies'}
                            </div>}
                          {activity.category && <div className="text-sm text-gray-600 dark:text-gray-300" data-id="element-4073">
                              {activity.category}
                            </div>}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>}
        {activeTab === 'progress' && <div className="space-y-8" data-id="element-4074">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4075">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-4076">
                {isArabic ? 'تقدم التعلم' : 'Learning Progress'}
              </h2>
              {/* Progress charts and detailed stats would go here */}
              <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-4077">
                {isArabic ? 'سيتم عرض مخططات التقدم التفصيلية هنا' : 'Detailed progress charts will be displayed here'}
              </div>
            </div>
          </div>}
        {activeTab === 'achievements' && <div className="space-y-8" data-id="element-4078">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4079">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-4080">
                {isArabic ? 'الإنجازات والشارات' : 'Achievements & Badges'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" data-id="element-4081">
                {userData.badges.map(badge => <div key={badge.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex flex-col items-center text-center" data-id="element-4082">
                    <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-3" data-id="element-4083">
                      <TrophyIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" data-id="element-4084" />
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-4085">
                      {badge.name}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4086">
                      {badge.description}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4087">
                      {isArabic ? 'تم الحصول عليها ' : 'Earned '} {badge.date}
                    </div>
                  </div>)}
                {/* Locked badges */}
                {[1, 2, 3].map(i => <div key={`locked-${i}`} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex flex-col items-center text-center opacity-60" data-id="element-4088">
                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center mb-3" data-id="element-4089">
                      <LockIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" data-id="element-4090" />
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-4091">
                      {isArabic ? 'شارة مقفلة' : 'Locked Badge'}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4092">
                      {isArabic ? 'أكمل المزيد من الدروس لفتح هذه الشارة' : 'Complete more lessons to unlock this badge'}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
        {activeTab === 'activity' && <div className="space-y-8" data-id="element-4093">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4094">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-4095">
                {isArabic ? 'سجل النشاط' : 'Activity Log'}
              </h2>
              <div className="relative" data-id="element-4096">
                {/* Timeline line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" data-id="element-4097"></div>
                <div className="space-y-6" data-id="element-4098">
                  {[...userData.recentActivity, {
                id: 5,
                type: 'lesson',
                title: 'Present Continuous Tense',
                date: '2 weeks ago',
                score: '90%'
              }, {
                id: 6,
                type: 'forum',
                title: 'Started discussion "Help with pronunciation"',
                date: '2 weeks ago',
                replies: 5
              }, {
                id: 7,
                type: 'exercise',
                title: 'Vocabulary Quiz: Business Terms',
                date: '3 weeks ago',
                score: '75%'
              }, {
                id: 8,
                type: 'vocabulary',
                title: 'Added 15 new words',
                date: '3 weeks ago',
                category: 'Travel Phrases'
              }].map(activity => <div key={activity.id} className="flex items-start gap-6" data-id="element-4099">
                      <div className="relative z-10" data-id="element-4100">
                        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-4101">
                          {activity.type === 'lesson' && <BookOpenIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-4102" />}
                          {activity.type === 'exercise' && <ClipboardCheckIcon className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-4103" />}
                          {activity.type === 'forum' && <MessageSquareIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-4104" />}
                          {activity.type === 'vocabulary' && <BookIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" data-id="element-4105" />}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex-1" data-id="element-4106">
                        <div className="flex items-start justify-between mb-2" data-id="element-4107">
                          <div className="font-medium text-gray-900 dark:text-white" data-id="element-4108">
                            {activity.title}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4109">
                            {activity.date}
                          </div>
                        </div>
                        <div className="flex items-center gap-4" data-id="element-4110">
                          {activity.score && <div className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-1" data-id="element-4111">
                              <StarIcon className="w-4 h-4" data-id="element-4112" />
                              {activity.score}
                            </div>}
                          {activity.replies && <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1" data-id="element-4113">
                              <MessageSquareIcon className="w-4 h-4" data-id="element-4114" />
                              {activity.replies} {isArabic ? 'ردود' : 'replies'}
                            </div>}
                          {activity.category && <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1" data-id="element-4115">
                              <TagIcon className="w-4 h-4" data-id="element-4116" />
                              {activity.category}
                            </div>}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}
// Additional icons needed
function BookIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-4117">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" data-id="element-4118" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" data-id="element-4119" />
    </svg>;
}
function ClipboardCheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-4120">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" data-id="element-4121" />
      <rect x="9" y="3" width="6" height="4" rx="2" data-id="element-4122" />
      <path d="m9 14 2 2 4-4" data-id="element-4123" />
    </svg>;
}
function TagIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-4124">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" data-id="element-4125" />
      <line x1="7" y1="7" x2="7.01" y2="7" data-id="element-4126" />
    </svg>;
}
function LockIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-4127">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-4128" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" data-id="element-4129" />
    </svg>;
}